import React, {
  useState,
  ForwardRefRenderFunction,
  forwardRef,
  useImperativeHandle,
  useCallback,
  useEffect,
} from "react";

import { Grid, IconButton, Modal } from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { isBoolean } from "lodash";
import { useTranslation } from "react-i18next";
import { FaRegCopy, FaCheck } from "react-icons/fa";
import { FiSend, FiShare2 } from "react-icons/fi";
import { isValidPhoneNumber } from "react-phone-number-input";
import isEmail from "validator/lib/isEmail";
import * as yup from "yup";

import { useAppState } from "../../../../context/StateContext";
import useYupValidation from "../../../../hooks/useYupValidation";
import { Customer } from "../../../../models/customer";
import { Error } from "../../../../models/error";
import { SharingData } from "../../../../models/share";
import TaskService from "../../../../services/task.service";
import { handleValidation } from "../../../../util/formValidation";
import { isShipmentTracking } from "../../../../util/validation";
import Button, { Color } from "../../../Form/Button";
import Input from "../../../Form/Input";
import PhoneInput from "../../../Form/PhoneInput";
import { NotificationToast } from "../../../Generic/Toast/NotificationToast";
import useStyles from "./styles";

export interface ShareLinkModalHandles {
  open: () => void;
  close: () => void;
}
interface ShareLinkModalProps {
  link: string;
  customer?: Customer;
  taskId?: number;
  isOpenModal?: boolean;
  isLoading?: boolean;
  onCloseModal?: () => void;
  sendShareLinkEmail?: ({ emails }: { emails: string[] }) => void;
}

enum ShareType {
  LINK = "link",
  SMS = "sms",
  EMAIL = "email",
}

const ShareLinkModal: ForwardRefRenderFunction<ShareLinkModalHandles, ShareLinkModalProps> = (
  props,
  ref
) => {
  const { link, customer, taskId, isOpenModal, isLoading, onCloseModal, sendShareLinkEmail } =
    props;
  const { t: translate } = useTranslation();
  const classes = useStyles();
  const { currentUser } = useAppState();

  const [shareType, setShareType] = useState<string>(
    isShipmentTracking(currentUser?.company) ? ShareType.EMAIL : ShareType.LINK
  );
  const [copied, setCopied] = useState<boolean>(false);
  const [isLinkShared, setIsLinkShared] = useState<boolean>(false);
  const [emailOrSmsSent, setEmailOrSmsSent] = useState<boolean>(false);
  const [hasEmailFormSubmitted, setHasEmailFormSubmitted] = useState<boolean>(false);
  const [hasPhoneFormSubmitted, setHasPhoneFormSubmitted] = useState<boolean>(false);
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);

  const [email, setEmail] = useState<string>(customer?.email || "");
  const [sms, setSMS] = useState<string>(customer?.phone || "");

  const [modalOpened, setModalOpened] = useState(false);

  const openModal = useCallback(() => {
    if (customer) {
      if (customer?.email) setEmail(customer.email);
      if (customer?.phone) setSMS(customer.phone);
    }

    setModalOpened(true);
  }, []);
  const closeModal = useCallback(() => {
    setShareType(ShareType.LINK);
    setCopied(false);
    setIsLinkShared(false);
    setEmailOrSmsSent(false);
    setEmail("");
    setSMS("");
    setIsLinkShared(false);
    setModalOpened(false);
    setHasEmailFormSubmitted(false);
    setHasPhoneFormSubmitted(false);
    setErrors([]);
    if (onCloseModal) onCloseModal();
  }, []);

  useImperativeHandle(ref, () => ({
    open: openModal,
    close: closeModal,
  }));

  const copyToClipboard = () => {
    if (link) {
      navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
        setIsLinkShared(true);
      }, 500);

      setTimeout(() => {
        setIsLinkShared(false);
      }, 2000);
    }
  };

  useEffect(() => {
    if (isBoolean(isOpenModal)) {
      if (isOpenModal && customer) {
        if (customer?.email) setEmail(customer.email);
        if (customer?.phone) setSMS(customer.phone);
      }

      setModalOpened(isOpenModal);
    }
  }, [isOpenModal]);

  useEffect(() => {
    if (modalOpened && customer) {
      if (customer?.email) setEmail(customer.email);
      if (customer?.phone) setSMS(customer.phone);
    }
  }, [modalOpened, customer]);

  const { fieldHasError, getErrorByField } = useYupValidation(errors);

  const schema = yup.object().shape({
    isTypeEmail: yup.boolean(),
    email: yup.string().when("isTypeEmail", {
      is: true,
      then: yup
        .string()
        .required(translate("thisFieldIsRequired"))
        .typeError(translate("thisFieldIsRequired"))
        .test({
          name: "validate-one-email",
          message: translate("thisEmailIsInvalid"),
          test: (value) => {
            if (value?.includes(",")) return true;

            return isEmail(value || "");
          },
        })
        .test({
          name: "validate-many-emails",
          message: translate("someOfTheseEmailsProvidedIsInvalid"),
          test: (value) => {
            if (!value?.includes(",")) return true;
            const emailSplited = value.split(",");
            let emailValid = false;

            for (const email of emailSplited) {
              if (email && email.trim()) {
                emailValid = isEmail(email.trim());
              }
            }

            return emailValid;
          },
        }),
    }),
    isTypePhone: yup.boolean(),
    phone: yup.string().when("isTypePhone", {
      is: true,
      then: yup
        .string()
        .required(translate("thisFieldIsRequired"))
        .typeError(translate("thisFieldIsRequired"))
        .test({
          name: "validate-phone",
          message: translate("pleaseEnterAValidPhoneNumber"),
          test: (value) => {
            return isValidPhoneNumber(value || "");
          },
        }),
    }),
  });

  useEffect(() => {
    if (hasEmailFormSubmitted && shareType === ShareType.EMAIL) {
      const validateSyncObject = {
        isTypeEmail: true,
        email,
      };

      handleValidation({ validateSyncObject, schema, updateErrors: setErrors });
    }
  }, [email, shareType, hasEmailFormSubmitted]);

  useEffect(() => {
    if (hasPhoneFormSubmitted && shareType === ShareType.SMS) {
      const validateSyncObject = {
        isTypePhone: true,
        phone: sms,
      };

      handleValidation({ validateSyncObject, schema, updateErrors: setErrors });
    }
  }, [sms, shareType, hasPhoneFormSubmitted]);

  const { mutate: sendEmail, isLoading: loadingSendEmail } = useMutation(
    ["mail.send"],
    (shareOption: SharingData) => TaskService.share(shareOption, taskId),
    {
      onSuccess: () => {
        NotificationToast.success(
          shareType === ShareType.SMS
            ? translate("smsSentSuccesfuly")
            : translate("emailSentSuccesfuly")
        );
        setEmailOrSmsSent(true);
        setTimeout(() => {
          setEmailOrSmsSent(false);
        }, 500);
      },
      onError: (err: AxiosError<Error>) => {
        NotificationToast.error(err?.response?.data?.message);
      },
    }
  );

  const handleSendEmail = () => {
    setHasEmailFormSubmitted(true);

    const emailsSplitted: string[] = email.split(",");

    const validateSyncObject = {
      email,
      isTypeEmail: shareType === ShareType.EMAIL,
    };

    if (
      emailsSplitted &&
      emailsSplitted.length > 0 &&
      handleValidation({ validateSyncObject, schema, updateErrors: setErrors })
    ) {
      if (isShipmentTracking(currentUser?.company)) {
        if (sendShareLinkEmail)
          sendShareLinkEmail({ emails: emailsSplitted.map((email) => email?.trim()) });
      } else {
        sendEmail({ emails: emailsSplitted.map((email) => email?.trim()) });
      }
    }
  };

  const handleSendSMS = () => {
    setHasPhoneFormSubmitted(true);

    const phones: string[] = sms.split(",");

    const validateSyncObject = {
      phone: sms,
      isTypePhone: shareType === ShareType.SMS,
    };

    if (handleValidation({ validateSyncObject, schema, updateErrors: setErrors })) {
      sendEmail({ phones });
    }
  };

  const renderLinkComponent = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={12} md={12} lg={12}>
        <Input name="copyLink" value={link} disabled />
      </Grid>
      <Grid item container md={12} lg={12} justifyContent="flex-end">
        <Button
          onClick={copyToClipboard}
          customColor={Color.SEND}
          className={classes.buttonShare}
          loading={copied}
        >
          {isLinkShared ? (
            <FaCheck size={14} color="#465869" className="mr-2" />
          ) : (
            <FaRegCopy size={14} color="#465869" className="mr-2" />
          )}
          {isLinkShared ? translate("copied") : translate("copy")}
        </Button>
      </Grid>
    </Grid>
  );

  useEffect(() => {
    setEmail(customer?.email ?? "");
  }, [customer]);

  const renderEmailComponent = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={12} md={12} lg={12}>
        <Input
          name="emailLink"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          type="email"
          placeholder={translate("contactEmailCom")}
          error={fieldHasError("email")}
          errorMessage={getErrorByField("email")}
        />
      </Grid>
      <Grid item container md={12} lg={12} justifyContent="flex-end">
        <Button
          disabled={!email || email.length === 0 || fieldHasError("email") || isLoading}
          onClick={handleSendEmail}
          customColor={Color.SEND}
          className={classes.buttonShare}
          loading={loadingSendEmail || isLoading}
        >
          {emailOrSmsSent ? (
            <FaCheck size={14} color="#465869" className="mr-2" />
          ) : (
            <FiSend size={14} color="#465869" className="mr-2" />
          )}
          {emailOrSmsSent ? translate("sent") : translate("send")}
        </Button>
      </Grid>
    </Grid>
  );

  const renderSMSComponent = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={12} md={12} lg={12}>
        <PhoneInput
          name="smsLink"
          value={sms || ""}
          onChange={(event) => setSMS(event)}
          error={fieldHasError("phone")}
          errorMessage={getErrorByField("phone")}
        />
      </Grid>
      <Grid item container md={12} lg={12} justifyContent="flex-end">
        <Button
          disabled={!sms || sms.length === 0 || fieldHasError("phone")}
          onClick={handleSendSMS}
          customColor={Color.SEND}
          className={classes.buttonShare}
          loading={loadingSendEmail}
        >
          {emailOrSmsSent ? (
            <FaCheck size={14} color="#465869" className="mr-2" />
          ) : (
            <FiSend size={14} color="#465869" className="mr-2" />
          )}
          {emailOrSmsSent ? translate("sent") : translate("send")}
        </Button>
      </Grid>
    </Grid>
  );

  const renderShareComponent = () => {
    switch (shareType) {
      case ShareType.LINK:
        return renderLinkComponent();
      case ShareType.SMS:
        return renderSMSComponent();
      case ShareType.EMAIL:
        return renderEmailComponent();
      default:
        return renderLinkComponent();
    }
  };

  const setActiveItem = (value: string): string => {
    if (value === shareType) return "active";

    return "";
  };

  return (
    <Modal open={modalOpened} onClose={closeModal}>
      <div className={classes.modal}>
        <section className={classes.content}>
          <Grid container direction="row" alignItems="center" justifyContent="space-between">
            <Grid item className={classes.badgeContent}>
              <div className={classes.shareBadge}>
                <FiShare2 size={22} color="#308CDB" />
              </div>
              <span className={classes.title}>{translate("share")}</span>
            </Grid>
            <IconButton onClick={closeModal} className={classes.closeButton}>
              <CloseIcon fontSize="medium" />
            </IconButton>
          </Grid>

          <h3 data-cy="shareLinkTitle" className={classes.subtitle}>
            {translate("shareTheVideoSurveyWithTheCustomer")}
          </h3>

          <ul data-cy="shareOptions" className={classes.shareTypeList}>
            {isShipmentTracking(currentUser?.company) ? (
              <>
                <li
                  data-cy="shareEmailOption"
                  className={setActiveItem(ShareType.EMAIL)}
                  onClick={() => setShareType(ShareType.EMAIL)}
                >
                  {translate("shareViaEMail")}
                </li>
                <li
                  data-cy="shareSmsOption"
                  className={setActiveItem(ShareType.SMS)}
                  onClick={() => setShareType(ShareType.SMS)}
                >
                  {translate("shareViaSms")}
                </li>
              </>
            ) : (
              <>
                <li
                  data-cy="shareLinkOption"
                  className={setActiveItem(ShareType.LINK)}
                  onClick={() => setShareType(ShareType.LINK)}
                >
                  {translate("shareViaLink")}
                </li>
                <li
                  data-cy="shareSmsOption"
                  className={setActiveItem(ShareType.SMS)}
                  onClick={() => setShareType(ShareType.SMS)}
                >
                  {translate("shareViaSms")}
                </li>
                <li
                  data-cy="shareEmailOption"
                  className={setActiveItem(ShareType.EMAIL)}
                  onClick={() => setShareType(ShareType.EMAIL)}
                >
                  {translate("shareViaEMail")}
                </li>
              </>
            )}
          </ul>

          {renderShareComponent()}
        </section>
      </div>
    </Modal>
  );
};

export default forwardRef(ShareLinkModal);
